import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { formatCurrency, formatCaneRate, formatDate, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Calendar,
  Plus,
  Eye,
  Trash2,
  Loader2,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  UserCircle,
  X,
  Edit,
  Download,
  Settings,
  ChevronLeft,
  Check,
  Truck,
  Users,
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function PayrollPeriodsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [periods, setPeriods] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ownerSetupOpen, setOwnerSetupOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [saving, setSaving] = useState(false);

  // View toggle
  const [view, setView] = useState(searchParams.get('view') || 'weekly');

  // Monthly view state
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  // Tenant settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tenantSettings, setTenantSettings] = useState({ ssb_business_number: '', ssb_employer_number: '' });
  const [savingSettings, setSavingSettings] = useState(false);

  // Edit owner entry
  const [editingOwnerIndex, setEditingOwnerIndex] = useState(null);

  // Period form data
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    workers_rate: '1.0',
  });

  // Owner entries: array of { owner_id, owner_name, dates: [{ date, cane_rate, tons }] }
  const [ownerEntries, setOwnerEntries] = useState([]);

  // Current owner being added/edited
  const [currentOwner, setCurrentOwner] = useState({
    owner_id: '',
    dates: [{ date: '' }],
  });

  // Daily Entry state
  const [dailyEntries, setDailyEntries] = useState({});
  // Structure: { "ownerId_date": { tons, grabs, workers: [], grabman: [], drivers: [], trucks: [] } }

  const [dailyEntryPopup, setDailyEntryPopup] = useState({
    open: false,
    ownerId: null,
    ownerName: '',
    date: null,
    dateIndex: null,
  });

  // Current daily entry being edited in popup
  const [currentDailyEntry, setCurrentDailyEntry] = useState({
    tons: '',
    grabs: '',
    workers: [],
    grabman: [],
    drivers: [],
    trucks: [],
  });

  // Available workers and truckers for selection
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [availableTruckers, setAvailableTruckers] = useState([]);

  // Inline creation dialogs
  const [addWorkerOpen, setAddWorkerOpen] = useState(false);
  const [addTruckerOpen, setAddTruckerOpen] = useState(false);
  const [newWorkerType, setNewWorkerType] = useState('cane_cutter'); // cane_cutter, grabman, driver
  const [newWorker, setNewWorker] = useState({ name: '', ssb_number: '', has_ssb: true, rate: '' });
  const [newTrucker, setNewTrucker] = useState({ name: '', truck_number: '', rate: '1.0' });
  const [savingInline, setSavingInline] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setSearchParams(view === 'weekly' ? {} : { view });
  }, [view, setSearchParams]);

  const fetchMonthlyData = useCallback(async () => {
    setMonthlyLoading(true);
    try {
      const res = await axios.get(`${API_URL}/payroll-periods/monthly-summary`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setMonthlyData(res.data);
    } catch (error) {
      toast.error('Failed to fetch monthly data');
      setMonthlyData(null);
    } finally {
      setMonthlyLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    if (view === 'monthly') {
      fetchMonthlyData();
    }
  }, [view, fetchMonthlyData]);

  const fetchData = async () => {
    try {
      const [periodsRes, ownersRes, workersRes, truckersRes] = await Promise.all([
        axios.get(`${API_URL}/payroll-periods`),
        axios.get(`${API_URL}/owners?is_active=true`),
        axios.get(`${API_URL}/workers?is_active=true`),
        axios.get(`${API_URL}/truckers?is_active=true`),
      ]);
      setPeriods(periodsRes.data);
      setOwners(ownersRes.data);
      setAvailableWorkers(workersRes.data);
      setAvailableTruckers(truckersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/tenant/settings`);
      setTenantSettings(res.data);
      return res.data;
    } catch (error) {
      return null;
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API_URL}/tenant/settings`, tenantSettings);
      setTenantSettings(res.data);
      toast.success('SSB settings saved');
      setSettingsOpen(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSSBExport = async () => {
    // Check tenant settings first
    const settings = await fetchTenantSettings();
    if (!settings || !settings.ssb_business_number || !settings.ssb_employer_number) {
      setTenantSettings(settings || { ssb_business_number: '', ssb_employer_number: '' });
      setSettingsOpen(true);
      toast.error('Please configure SSB Business Number and Employer Number before exporting');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/payroll-periods/monthly-ssb-export`, {
        params: { year: selectedYear, month: selectedMonth },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SSB_Report_${MONTHS[selectedMonth - 1]}_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('SSB report exported successfully');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No payroll periods found for this month');
      } else {
        toast.error('Failed to export SSB report');
      }
    }
  };

  const handleOpenDialog = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const weekNum = Math.ceil((today.getDate() + new Date(today.getFullYear(), today.getMonth(), 1).getDay()) / 7);
    const monthName = today.toLocaleString('default', { month: 'short' });

    setFormData({
      name: `Week ${weekNum} - ${monthName} ${today.getFullYear()}`,
      start_date: startOfWeek.toISOString().split('T')[0],
      end_date: endOfWeek.toISOString().split('T')[0],
      workers_rate: '1.0',
    });
    setOwnerEntries([]);
    setEditingOwnerIndex(null);
    setDailyEntries({});  // Reset daily entries
    setDialogOpen(true);
  };

  const handleOpenOwnerSetup = (editIndex = null) => {
    if (editIndex !== null) {
      const entry = ownerEntries[editIndex];
      setCurrentOwner({
        owner_id: entry.owner_id,
        dates: entry.dates.map(d => ({
          date: d.date,
        })),
      });
      setEditingOwnerIndex(editIndex);
    } else {
      setCurrentOwner({
        owner_id: '',
        dates: [{ date: formData.start_date || '' }],
      });
      setEditingOwnerIndex(null);
    }
    setOwnerSetupOpen(true);
  };

  const handleAddDateRow = () => {
    setCurrentOwner(prev => ({
      ...prev,
      dates: [...prev.dates, { date: '' }],
    }));
  };

  const handleRemoveDateRow = (index) => {
    setCurrentOwner(prev => ({
      ...prev,
      dates: prev.dates.filter((_, i) => i !== index),
    }));
  };

  const handleDateChange = (index, field, value) => {
    setCurrentOwner(prev => ({
      ...prev,
      dates: prev.dates.map((d, i) => i === index ? { ...d, [field]: value } : d),
    }));
  };

  const handleAddOwnerEntry = () => {
    if (!currentOwner.owner_id) {
      toast.error('Please select an owner');
      return;
    }

    const validDates = currentOwner.dates.filter(d => d.date);
    if (validDates.length === 0) {
      toast.error('Please add at least one delivery date');
      return;
    }

    const selectedOwner = owners.find(o => o.id === currentOwner.owner_id);

    const newEntry = {
      owner_id: currentOwner.owner_id,
      owner_name: selectedOwner?.name || 'Unknown',
      owner_rate: selectedOwner?.rate || 1.0,
      dates: validDates.map((d, i) => {
        const key = getDailyEntryKey(currentOwner.owner_id, d.date, i);
        const dailyData = dailyEntries[key];
        return {
          date: d.date,
          cane_rate: dailyData?.tons ? parseFloat(dailyData.tons) : 0,
          tons: dailyData?.grabs ? parseFloat(dailyData.grabs) : 1,
        };
      }),
    };

    if (editingOwnerIndex !== null) {
      setOwnerEntries(prev => prev.map((entry, idx) =>
        idx === editingOwnerIndex ? newEntry : entry
      ));
      toast.success(`${selectedOwner?.name} updated`);
    } else {
      setOwnerEntries(prev => [...prev, newEntry]);
      toast.success(`${selectedOwner?.name} added with ${validDates.length} date(s)`);
    }

    setCurrentOwner({
      owner_id: '',
      dates: [{ date: formData.start_date || '' }],
    });
    setEditingOwnerIndex(null);
    setOwnerSetupOpen(false);
  };

  const handleRemoveOwnerEntry = (index) => {
    setOwnerEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleCloseOwnerSetup = () => {
    setOwnerSetupOpen(false);
    setEditingOwnerIndex(null);
  };

  // Daily Entry popup functions
  const getDailyEntryKey = (ownerId, date, dateIndex) => `${ownerId}_${date}_${dateIndex}`;

  const hasDailyEntry = (ownerId, date, dateIndex) => {
    const key = getDailyEntryKey(ownerId, date, dateIndex);
    return dailyEntries[key] && (
      dailyEntries[key].tons ||
      dailyEntries[key].grabs ||
      dailyEntries[key].workers?.length > 0 ||
      dailyEntries[key].grabman?.length > 0 ||
      dailyEntries[key].drivers?.length > 0 ||
      dailyEntries[key].trucks?.length > 0
    );
  };

  const openDailyEntryPopup = (ownerId, ownerName, date, dateIndex) => {
    const key = getDailyEntryKey(ownerId, date, dateIndex);
    const existingEntry = dailyEntries[key] || {
      tons: '',
      grabs: '',
      workers: [],
      grabman: [],
      drivers: [],
      trucks: [],
    };
    setCurrentDailyEntry(existingEntry);
    setDailyEntryPopup({
      open: true,
      ownerId,
      ownerName,
      date,
      dateIndex,
    });
  };

  const closeDailyEntryPopup = () => {
    setDailyEntryPopup({ open: false, ownerId: null, ownerName: '', date: null, dateIndex: null });
  };

  const saveDailyEntry = () => {
    const key = getDailyEntryKey(dailyEntryPopup.ownerId, dailyEntryPopup.date, dailyEntryPopup.dateIndex);
    setDailyEntries(prev => ({
      ...prev,
      [key]: { ...currentDailyEntry }
    }));
    toast.success('Daily entry saved');
    closeDailyEntryPopup();
  };

  const updateCurrentDailyEntry = (field, value) => {
    setCurrentDailyEntry(prev => ({ ...prev, [field]: value }));
  };

  // Worker selection toggle
  const toggleWorkerSelection = (workerId, section) => {
    setCurrentDailyEntry(prev => {
      const currentList = prev[section] || [];
      const exists = currentList.some(w => w.worker_id === workerId);
      if (exists) {
        return { ...prev, [section]: currentList.filter(w => w.worker_id !== workerId) };
      } else {
        return { ...prev, [section]: [...currentList, { worker_id: workerId, tons: 0 }] };
      }
    });
  };

  // Trucker selection toggle
  const toggleTruckerSelection = (truckerId) => {
    setCurrentDailyEntry(prev => {
      const currentList = prev.trucks || [];
      const exists = currentList.some(t => t.trucker_id === truckerId);
      if (exists) {
        return { ...prev, trucks: currentList.filter(t => t.trucker_id !== truckerId) };
      } else {
        const trucker = availableTruckers.find(t => t.id === truckerId);
        return { ...prev, trucks: [...currentList, { trucker_id: truckerId, truck_number: trucker?.truck_number || '', tons: 0 }] };
      }
    });
  };

  // Update worker tons in daily entry
  const updateWorkerTons = (workerId, tons) => {
    setCurrentDailyEntry(prev => ({
      ...prev,
      workers: (prev.workers || []).map(w =>
        w.worker_id === workerId ? { ...w, tons: parseFloat(tons) || 0 } : w
      )
    }));
  };

  // Inline worker creation
  const handleAddNewWorker = (workerType) => {
    setNewWorkerType(workerType);
    setNewWorker({ name: '', ssb_number: '', has_ssb: true, rate: '' });
    setAddWorkerOpen(true);
  };

  const handleSaveNewWorker = async () => {
    if (!newWorker.name.trim()) {
      toast.error('Please enter a worker name');
      return;
    }

    // Validate rate for grabman/driver
    if ((newWorkerType === 'grabman' || newWorkerType === 'driver') && !newWorker.rate) {
      toast.error('Please enter a rate for this worker type');
      return;
    }

    setSavingInline(true);
    try {
      const response = await axios.post(`${API_URL}/workers/quick-create`, {
        name: newWorker.name,
        worker_type: newWorkerType,
        ssb_number: newWorker.ssb_number || null,
        has_ssb: newWorker.has_ssb,
        rate: newWorkerType === 'grabman' || newWorkerType === 'driver' ? parseFloat(newWorker.rate) : null,
      });

      // Add to available workers
      setAvailableWorkers(prev => [...prev, response.data]);

      // Auto-select the new worker
      if (newWorkerType === 'cane_cutter' || newWorkerType === 'field_personnel') {
        toggleWorkerSelection(response.data.id, 'workers');
      } else if (newWorkerType === 'grabman') {
        toggleWorkerSelection(response.data.id, 'grabman');
      } else if (newWorkerType === 'driver') {
        toggleWorkerSelection(response.data.id, 'drivers');
      }

      toast.success(`${newWorker.name} created and selected`);
      setAddWorkerOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create worker');
    } finally {
      setSavingInline(false);
    }
  };

  // Inline trucker creation
  const handleAddNewTrucker = () => {
    setNewTrucker({ name: '', truck_number: '', rate: '1.0' });
    setAddTruckerOpen(true);
  };

  const handleSaveNewTrucker = async () => {
    if (!newTrucker.name.trim()) {
      toast.error('Please enter a trucker name');
      return;
    }

    setSavingInline(true);
    try {
      const response = await axios.post(`${API_URL}/truckers/quick-create`, {
        name: newTrucker.name,
        truck_number: newTrucker.truck_number || null,
        rate: parseFloat(newTrucker.rate) || 1.0,
      });

      // Add to available truckers
      setAvailableTruckers(prev => [...prev, response.data]);

      // Auto-select the new trucker
      toggleTruckerSelection(response.data.id);

      toast.success(`${newTrucker.name} created and selected`);
      setAddTruckerOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create trucker');
    } finally {
      setSavingInline(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (ownerEntries.length === 0) {
      toast.error('Please add at least one owner with dates');
      return;
    }

    setSaving(true);

    try {
      // Transform owner entries to include daily entry data (tons/grabs from daily entries)
      const ownerEntriesWithDailyData = ownerEntries.map(entry => ({
        ...entry,
        dates: entry.dates.map((d, dIdx) => {
          const key = getDailyEntryKey(entry.owner_id, d.date, dIdx);
          const dailyData = dailyEntries[key];
          return {
            date: d.date,
            cane_rate: dailyData?.tons ? parseFloat(dailyData.tons) : 0,
            tons: dailyData?.grabs ? parseFloat(dailyData.grabs) : 1,
            workers: dailyData?.workers || null,
            grabman: dailyData?.grabman || null,
            drivers: dailyData?.drivers || null,
            trucks: dailyData?.trucks || null,
          };
        }),
      }));

      const response = await axios.post(`${API_URL}/payroll-periods/with-owners`, {
        name: formData.name,
        start_date: formData.start_date,
        end_date: formData.end_date,
        workers_rate: parseFloat(formData.workers_rate) || 1.0,
        owner_entries: ownerEntriesWithDailyData,
      });

      toast.success('Payroll period created with owner schedules');
      setDialogOpen(false);
      navigate(`/payroll/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payroll period');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/payroll-periods/${selectedPeriod.id}`);
      toast.success('Payroll period deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedPeriod(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payroll period');
    }
  };

  const handleExport = async (periodId, format) => {
    try {
      const response = await axios.get(`${API_URL}/payroll-periods/${periodId}/export/${format}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_export.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  // Calculate total dates across all owners
  const totalDates = ownerEntries.reduce((sum, entry) => sum + entry.dates.length, 0);

  // Get unique dates for preview
  const uniqueDates = [...new Set(ownerEntries.flatMap(e => e.dates.map(d => d.date)))].sort();

  // Generate year options
  const yearOptions = [];
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) {
    yearOptions.push(y);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Helper to get week labels from monthly data
  const weekPeriods = monthlyData?.weekly_periods || [];

  const renderMonthlyView = () => {
    return (
      <div className="space-y-6">
        {/* Month/Year Selector */}
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Button variant="outline" size="icon" onClick={() => {
                  if (selectedMonth === 1) {
                    setSelectedMonth(12);
                    setSelectedYear(y => y - 1);
                  } else {
                    setSelectedMonth(m => m - 1);
                  }
                }}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex gap-2">
                  <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="icon" onClick={() => {
                  if (selectedMonth === 12) {
                    setSelectedMonth(1);
                    setSelectedYear(y => y + 1);
                  } else {
                    setSelectedMonth(m => m + 1);
                  }
                }}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1" onClick={() => {
                  fetchTenantSettings().then(s => {
                    if (s) setTenantSettings(s);
                    setSettingsOpen(true);
                  });
                }}>
                  <Settings className="w-4 h-4" />
                  SSB Settings
                </Button>
                <Button size="sm" className="gap-1" onClick={handleSSBExport} disabled={!monthlyData || weekPeriods.length === 0}>
                  <Download className="w-4 h-4" />
                  Export SSB Report
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {monthlyLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : !monthlyData || weekPeriods.length === 0 ? (
          <Card className="card-base">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                No payroll periods found for {MONTHS[selectedMonth - 1]} {selectedYear}.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Month Header Card */}
            <Card className="card-base">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold">{monthlyData.month} {monthlyData.year}</h2>
                    <p className="text-sm text-muted-foreground">{weekPeriods.length} weekly period(s)</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div className="text-center">
                      <p className="text-muted-foreground">Total Gross</p>
                      <p className="font-semibold text-lg">{formatCurrency(monthlyData.month_totals.total_gross)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-muted-foreground">SSB (Employee)</p>
                      <p className="font-semibold text-lg text-secondary">{formatCurrency(monthlyData.month_totals.total_ssb_employee)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-muted-foreground">SSB (Employer)</p>
                      <p className="font-semibold text-lg text-secondary">{formatCurrency(monthlyData.month_totals.total_ssb_employer)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-muted-foreground">Total Net</p>
                      <p className="font-semibold text-lg text-primary">{formatCurrency(monthlyData.month_totals.total_net)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Workers Summary Table */}
            {Object.keys(monthlyData.workers_summary).length > 0 && (
              <Card className="card-base">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-3">Workers Summary (Cane Cutters / Field Personnel)</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[150px]">Worker</TableHead>
                          {weekPeriods.map((wp, i) => (
                            <TableHead key={wp.id} className="text-right min-w-[100px]">Week {i + 1}</TableHead>
                          ))}
                          <TableHead className="text-right min-w-[100px] font-bold">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.values(monthlyData.workers_summary).map((worker) => (
                          <TableRow key={worker.worker_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{worker.worker_name}</p>
                                {worker.ssb_number && <p className="text-xs text-muted-foreground">SSN: {worker.ssb_number}</p>}
                              </div>
                            </TableCell>
                            {weekPeriods.map((wp) => {
                              const week = worker.weeks[wp.id];
                              return (
                                <TableCell key={wp.id} className="text-right">
                                  {week ? (
                                    <div>
                                      <p>{formatCurrency(week.gross_pay)}</p>
                                      {week.ssb_employee > 0 && <p className="text-xs text-muted-foreground">SSB: {formatCurrency(week.ssb_employee)}</p>}
                                    </div>
                                  ) : <span className="text-muted-foreground">-</span>}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-right font-semibold">
                              <div>
                                <p>{formatCurrency(worker.totals.gross_pay)}</p>
                                {worker.totals.ssb_employee > 0 && <p className="text-xs text-muted-foreground">SSB: {formatCurrency(worker.totals.ssb_employee)}</p>}
                                <p className="text-xs text-primary">Net: {formatCurrency(worker.totals.net_pay)}</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Grabmen Summary Table */}
            {Object.keys(monthlyData.grabmen_summary).length > 0 && (
              <Card className="card-base">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-3">Grabmen Summary</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[150px]">Grabman</TableHead>
                          {weekPeriods.map((wp, i) => (
                            <TableHead key={wp.id} className="text-right min-w-[100px]">Week {i + 1}</TableHead>
                          ))}
                          <TableHead className="text-right min-w-[100px] font-bold">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.values(monthlyData.grabmen_summary).map((worker) => (
                          <TableRow key={worker.worker_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{worker.worker_name}</p>
                                <p className="text-xs text-muted-foreground">Rate: {worker.rate}x</p>
                              </div>
                            </TableCell>
                            {weekPeriods.map((wp) => {
                              const week = worker.weeks[wp.id];
                              return (
                                <TableCell key={wp.id} className="text-right">
                                  {week ? (
                                    <div>
                                      <p>{formatCurrency(week.gross_pay)}</p>
                                      {week.ssb_employee > 0 && <p className="text-xs text-muted-foreground">SSB: {formatCurrency(week.ssb_employee)}</p>}
                                    </div>
                                  ) : <span className="text-muted-foreground">-</span>}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-right font-semibold">
                              <div>
                                <p>{formatCurrency(worker.totals.gross_pay)}</p>
                                <p className="text-xs text-primary">Net: {formatCurrency(worker.totals.net_pay)}</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Drivers Summary Table */}
            {Object.keys(monthlyData.drivers_summary).length > 0 && (
              <Card className="card-base">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-3">Drivers Summary</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[150px]">Driver</TableHead>
                          {weekPeriods.map((wp, i) => (
                            <TableHead key={wp.id} className="text-right min-w-[100px]">Week {i + 1}</TableHead>
                          ))}
                          <TableHead className="text-right min-w-[100px] font-bold">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.values(monthlyData.drivers_summary).map((worker) => (
                          <TableRow key={worker.worker_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{worker.worker_name}</p>
                                <p className="text-xs text-muted-foreground">Rate: {worker.rate}x</p>
                              </div>
                            </TableCell>
                            {weekPeriods.map((wp) => {
                              const week = worker.weeks[wp.id];
                              return (
                                <TableCell key={wp.id} className="text-right">
                                  {week ? (
                                    <div>
                                      <p>{formatCurrency(week.gross_pay)}</p>
                                      {week.ssb_employee > 0 && <p className="text-xs text-muted-foreground">SSB: {formatCurrency(week.ssb_employee)}</p>}
                                    </div>
                                  ) : <span className="text-muted-foreground">-</span>}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-right font-semibold">
                              <div>
                                <p>{formatCurrency(worker.totals.gross_pay)}</p>
                                <p className="text-xs text-primary">Net: {formatCurrency(worker.totals.net_pay)}</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Truckers Summary Table */}
            {Object.keys(monthlyData.truckers_summary).length > 0 && (
              <Card className="card-base">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-3">Truckers Summary</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[150px]">Trucker</TableHead>
                          {weekPeriods.map((wp, i) => (
                            <TableHead key={wp.id} className="text-right min-w-[100px]">Week {i + 1}</TableHead>
                          ))}
                          <TableHead className="text-right min-w-[100px] font-bold">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.values(monthlyData.truckers_summary).map((trucker) => (
                          <TableRow key={trucker.trucker_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{trucker.trucker_name}</p>
                                {trucker.truck_number && <p className="text-xs text-muted-foreground">Truck: {trucker.truck_number}</p>}
                              </div>
                            </TableCell>
                            {weekPeriods.map((wp) => {
                              const week = trucker.weeks[wp.id];
                              return (
                                <TableCell key={wp.id} className="text-right">
                                  {week ? formatCurrency(week.total_amount) : <span className="text-muted-foreground">-</span>}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(trucker.totals.total_amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Owners Summary Table */}
            {Object.keys(monthlyData.owners_summary).length > 0 && (
              <Card className="card-base">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-3">Owners Summary</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[150px]">Owner</TableHead>
                          {weekPeriods.map((wp, i) => (
                            <TableHead key={wp.id} className="text-right min-w-[100px]">Week {i + 1}</TableHead>
                          ))}
                          <TableHead className="text-right min-w-[100px] font-bold">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.values(monthlyData.owners_summary).map((owner) => (
                          <TableRow key={owner.owner_id}>
                            <TableCell className="font-medium">{owner.owner_name}</TableCell>
                            {weekPeriods.map((wp) => {
                              const week = owner.weeks[wp.id];
                              return (
                                <TableCell key={wp.id} className="text-right">
                                  {week ? formatCurrency(week.total_amount) : <span className="text-muted-foreground">-</span>}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(owner.totals.total_amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Group Leader Summary Table */}
            {monthlyData.group_leader_summary && Object.keys(monthlyData.group_leader_summary.weeks).length > 0 && (
              <Card className="card-base border-primary/20">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-3">Group Leader Summary</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[150px]">Item</TableHead>
                          {weekPeriods.map((wp, i) => (
                            <TableHead key={wp.id} className="text-right min-w-[100px]">Week {i + 1}</TableHead>
                          ))}
                          <TableHead className="text-right min-w-[100px] font-bold">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">Group Leader Pay</TableCell>
                          {weekPeriods.map((wp) => {
                            const week = monthlyData.group_leader_summary.weeks[wp.id];
                            return (
                              <TableCell key={wp.id} className="text-right">
                                {week ? formatCurrency(week.group_leader_pay) : <span className="text-muted-foreground">-</span>}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right font-bold text-primary text-lg">
                            {formatCurrency(monthlyData.group_leader_summary.total_group_leader_pay)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Payroll Periods"
        subtitle={view === 'weekly' ? `${periods.length} payroll periods` : `${MONTHS[selectedMonth - 1]} ${selectedYear} Monthly Report`}
        actions={
          <Button onClick={handleOpenDialog} className="gap-2" data-testid="new-period-btn">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Period</span>
          </Button>
        }
      />

      <div className="flex-1 p-4 lg:p-8 space-y-4">
        {/* View Toggle */}
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="weekly">Weekly Periods</TabsTrigger>
            <TabsTrigger value="monthly">Monthly Report</TabsTrigger>
          </TabsList>

          <TabsContent value="weekly">
            <div className="space-y-4">
              {periods.map((period, index) => (
                <Card
                  key={period.id}
                  className="card-base animate-slide-up hover:shadow-md transition-shadow"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`period-card-${period.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{period.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(period.start_date)} - {formatDate(period.end_date)}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="flex gap-4 text-sm">
                          <div className="text-center">
                            <p className="text-muted-foreground">Gross</p>
                            <p className="font-semibold text-lg">{formatCurrency(period.total_gross)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-muted-foreground">SSB</p>
                            <p className="font-semibold text-lg text-secondary">{formatCurrency(period.total_ssb_employee)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-muted-foreground">Net</p>
                            <p className="font-semibold text-lg text-primary">{formatCurrency(period.total_net)}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className={`status-badge ${getStatusColor(period.status)}`}>
                            {period.status}
                          </span>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleExport(period.id, 'excel')}
                            title="Export Excel"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleExport(period.id, 'pdf')}
                            title="Export PDF"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>

                          {period.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => {
                                setSelectedPeriod(period);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}

                          <Link to={`/payroll/${period.id}`}>
                            <Button variant="outline" size="sm" className="gap-1">
                              <Eye className="w-4 h-4" />
                              View
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {periods.length === 0 && (
                <Card className="card-base">
                  <CardContent className="p-8 text-center">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">
                      No payroll periods yet. Create your first period to get started.
                    </p>
                    <Button onClick={handleOpenDialog} className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Payroll Period
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="monthly">
            {renderMonthlyView()}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Period Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Payroll Period</DialogTitle>
            <DialogDescription>
              Set up a new payroll period with owner schedules and tons.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Period Info */}
              <div className="space-y-2">
                <Label htmlFor="name">Period Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Week 1 - Jan 2025"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-4 py-4">
              {/* Workers Rate - Period Level */}
              <div className="space-y-2">
                <Label htmlFor="workers_rate">Workers Rate * (applies to all daily deliveries)</Label>
                <Input
                      id="workers_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.workers_rate}
                      onChange={(e) => setFormData({ ...formData, workers_rate: e.target.value })}
                      placeholder="1.0"
                    />
                <p className="text-xs text-muted-foreground">
                  This rate will be applied to all daily deliveries in this period.
                </p>
              </div>
              </div>
              {/* Owner Setup Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      <UserCircle className="w-5 h-5 text-primary" />
                      Owner Schedules
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Add owners with their delivery dates
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenOwnerSetup(null)}
                    className="gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Owner
                  </Button>
                </div>

                {/* Owner Entries List */}
                {ownerEntries.length > 0 ? (
                  <div className="space-y-2">
                    {ownerEntries.map((entry, index) => (
                      <div
                        key={index}
                        className="p-3 bg-muted/30 rounded-lg border border-border/50"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{entry.owner_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Rate: {entry.owner_rate}× • {entry.dates.length} delivery day(s)
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => handleOpenOwnerSetup(index)}
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive"
                              onClick={() => handleRemoveOwnerEntry(index)}
                              title="Remove"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-2 space-y-1">
                          {entry.dates.map((d, i) => {
                            const hasEntry = hasDailyEntry(entry.owner_id, d.date, i);
                            const key = getDailyEntryKey(entry.owner_id, d.date, i);
                            const dailyData = dailyEntries[key];
                            return (
                              <div key={i} className="flex items-center gap-2 p-1.5 bg-muted/20 rounded">
                                <span className="text-xs font-medium">
                                  {formatDate(d.date)}
                                </span>
                                {hasEntry && dailyData?.tons && (
                                  <span className="text-xs text-muted-foreground">
                                    {dailyData.tons} tons{dailyData.grabs ? ` • ${dailyData.grabs} grabs` : ''}
                                  </span>
                                )}
                                <div className="ml-auto">
                                  <Button
                                    type="button"
                                    variant={hasEntry ? "outline" : "default"}
                                    size="sm"
                                    className="h-6 text-xs gap-1 px-2"
                                    onClick={() => openDailyEntryPopup(entry.owner_id, entry.owner_name, d.date, i)}
                                  >
                                    {hasEntry ? (
                                      <>
                                        <Check className="w-3 h-3" />
                                        Edit
                                      </>
                                    ) : (
                                      <>
                                        <Plus className="w-3 h-3" />
                                        Entry
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 border border-dashed rounded-lg text-center text-muted-foreground">
                    <UserCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No owners added yet</p>
                    <p className="text-xs">Click &quot;Add Owner&quot; to set up delivery schedules</p>
                  </div>
                )}

                {/* Summary */}
                {ownerEntries.length > 0 && (
                  <div className="mt-3 p-3 bg-primary/5 rounded-lg text-sm">
                    <p className="font-medium">Summary:</p>
                    <p className="text-muted-foreground">
                      {ownerEntries.length} owner(s) • {totalDates} total delivery entries • {uniqueDates.length} unique dates
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Day entries will be auto-created for: {uniqueDates.map(d => formatDate(d)).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || ownerEntries.length === 0}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Period'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Owner Setup Dialog */}
      <Dialog open={ownerSetupOpen} onOpenChange={setOwnerSetupOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOwnerIndex !== null ? 'Edit Owner Schedule' : 'Add Owner Schedule'}</DialogTitle>
            <DialogDescription>
              Select an owner and add their delivery dates
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Owner Selection */}
            <div className="space-y-2">
              <Label>Owner *</Label>
              <Select
                value={currentOwner.owner_id}
                onValueChange={(value) => setCurrentOwner(prev => ({ ...prev, owner_id: value }))}
                disabled={editingOwnerIndex !== null}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {owners
                    .filter(o => editingOwnerIndex !== null || !ownerEntries.some(e => e.owner_id === o.id))
                    .map((owner) => (
                      <SelectItem key={owner.id} value={owner.id}>
                        {owner.name} (Rate: {owner.rate}×)
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date and Rate Entries */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Delivery Dates & Details</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleAddDateRow}
                  className="h-7 gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add Date
                </Button>
              </div>

              <div className="space-y-2">
                {currentOwner.dates.map((dateEntry, index) => {
                  const hasEntry = currentOwner.owner_id && dateEntry.date && hasDailyEntry(currentOwner.owner_id, dateEntry.date, index);
                  const key = currentOwner.owner_id && dateEntry.date ? getDailyEntryKey(currentOwner.owner_id, dateEntry.date, index) : null;
                  const dailyData = key ? dailyEntries[key] : null;
                  const ownerName = owners.find(o => o.id === currentOwner.owner_id)?.name || '';
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={dateEntry.date}
                        onChange={(e) => handleDateChange(index, 'date', e.target.value)}
                        className="flex-1"
                        min={formData.start_date}
                        max={formData.end_date}
                      />
                      {dateEntry.date && currentOwner.owner_id && (
                        <Button
                          type="button"
                          variant={hasEntry ? "outline" : "default"}
                          size="sm"
                          className="h-8 text-xs gap-1 px-2 flex-shrink-0"
                          onClick={() => openDailyEntryPopup(currentOwner.owner_id, ownerName, dateEntry.date, index)}
                        >
                          {hasEntry ? (
                            <>
                              <Check className="w-3 h-3" />
                              {dailyData?.tons ? `${dailyData.tons}t` : 'Edit'}
                            </>
                          ) : (
                            <>
                              <Plus className="w-3 h-3" />
                              Entry
                            </>
                          )}
                        </Button>
                      )}
                      {currentOwner.dates.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive flex-shrink-0"
                          onClick={() => handleRemoveDateRow(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Click Entry to add tons, grabs, workers, and trucks for each date
              </p>
            </div>

            {/* Preview */}
            {currentOwner.owner_id && currentOwner.dates.some(d => d.date) && (
              <div className="p-3 bg-accent/10 rounded-lg">
                <p className="text-sm font-medium">Preview:</p>
                <p className="text-xs text-muted-foreground">
                  {owners.find(o => o.id === currentOwner.owner_id)?.name} will have {currentOwner.dates.filter(d => d.date).length} delivery day(s)
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCloseOwnerSetup}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddOwnerEntry}
              disabled={!currentOwner.owner_id || !currentOwner.dates.some(d => d.date)}
            >
              {editingOwnerIndex !== null ? 'Update Owner' : 'Add Owner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SSB Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>SSB Settings</DialogTitle>
            <DialogDescription>
              Configure your Social Security Board business and employer numbers for SSB report exports.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ssb_business_number">SSB Business Number</Label>
              <Input
                id="ssb_business_number"
                value={tenantSettings.ssb_business_number}
                onChange={(e) => setTenantSettings(prev => ({ ...prev, ssb_business_number: e.target.value }))}
                placeholder="e.g. 003B1084"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ssb_employer_number">SSB Employer Number</Label>
              <Input
                id="ssb_employer_number"
                value={tenantSettings.ssb_employer_number}
                onChange={(e) => setTenantSettings(prev => ({ ...prev, ssb_employer_number: e.target.value }))}
                placeholder="e.g. 00GE20044"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSettingsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payroll Period</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedPeriod?.name}&quot;? This will also delete all payroll entries. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Daily Entry Popup */}
      <Dialog open={dailyEntryPopup.open} onOpenChange={(open) => !open && closeDailyEntryPopup()}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Daily Entry - {dailyEntryPopup.ownerName} - {formatDate(dailyEntryPopup.date)}
            </DialogTitle>
            <DialogDescription>
              Add tons, grabs, workers, grabmen, drivers, and trucks for this day
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Tons & Grabs Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="daily-tons">Tons</Label>
                <Input
                  id="daily-tons"
                  type="number"
                  step="0.0001"
                  min="0"
                  placeholder="39.8594"
                  value={currentDailyEntry.tons || ''}
                  onChange={(e) => updateCurrentDailyEntry('tons', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="daily-grabs">Grabs Delivered</Label>
                <Input
                  id="daily-grabs"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="1"
                  value={currentDailyEntry.grabs || ''}
                  onChange={(e) => updateCurrentDailyEntry('grabs', e.target.value)}
                />
              </div>
            </div>

            {/* Workers Section (Cane Cutters) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Workers (Cane Cutters)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddNewWorker('cane_cutter')}
                  className="gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add New
                </Button>
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-2">
                {availableWorkers
                  .filter(w => w.worker_type === 'cane_cutter' || w.worker_type === 'field_personnel')
                  .map(worker => {
                    const isSelected = (currentDailyEntry.workers || []).some(w => w.worker_id === worker.id);
                    const workerEntry = (currentDailyEntry.workers || []).find(w => w.worker_id === worker.id);
                    return (
                      <div key={worker.id} className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded">
                        <Checkbox
                          id={`worker-${worker.id}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleWorkerSelection(worker.id, 'workers')}
                        />
                        <label
                          htmlFor={`worker-${worker.id}`}
                          className="flex-1 text-sm cursor-pointer"
                        >
                          {worker.name}
                          {worker.ssb_number && <span className="text-xs text-muted-foreground ml-1">({worker.ssb_number})</span>}
                        </label>
                        {isSelected && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground">Tons:</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              className="w-20 h-7 text-xs"
                              value={workerEntry?.tons || ''}
                              onChange={(e) => updateWorkerTons(worker.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                {availableWorkers.filter(w => w.worker_type === 'cane_cutter' || w.worker_type === 'field_personnel').length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-2">No workers available</p>
                )}
              </div>
            </div>

            {/* Grabman Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Grabman</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddNewWorker('grabman')}
                  className="gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add New
                </Button>
              </div>
              <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-2">
                {availableWorkers
                  .filter(w => w.worker_type === 'grabman')
                  .map(worker => {
                    const isSelected = (currentDailyEntry.grabman || []).some(w => w.worker_id === worker.id);
                    return (
                      <div key={worker.id} className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded">
                        <Checkbox
                          id={`grabman-${worker.id}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleWorkerSelection(worker.id, 'grabman')}
                        />
                        <label
                          htmlFor={`grabman-${worker.id}`}
                          className="flex-1 text-sm cursor-pointer"
                        >
                          {worker.name}
                          <span className="text-xs text-muted-foreground ml-1">(Rate: {worker.rate}x)</span>
                        </label>
                      </div>
                    );
                  })}
                {availableWorkers.filter(w => w.worker_type === 'grabman').length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-2">No grabmen available</p>
                )}
              </div>
            </div>

            {/* Drivers Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Drivers</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddNewWorker('driver')}
                  className="gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add New
                </Button>
              </div>
              <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-2">
                {availableWorkers
                  .filter(w => w.worker_type === 'driver')
                  .map(worker => {
                    const isSelected = (currentDailyEntry.drivers || []).some(w => w.worker_id === worker.id);
                    return (
                      <div key={worker.id} className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded">
                        <Checkbox
                          id={`driver-${worker.id}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleWorkerSelection(worker.id, 'drivers')}
                        />
                        <label
                          htmlFor={`driver-${worker.id}`}
                          className="flex-1 text-sm cursor-pointer"
                        >
                          {worker.name}
                          <span className="text-xs text-muted-foreground ml-1">(Rate: {worker.rate}x)</span>
                        </label>
                      </div>
                    );
                  })}
                {availableWorkers.filter(w => w.worker_type === 'driver').length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-2">No drivers available</p>
                )}
              </div>
            </div>

            {/* Trucks Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Trucks
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddNewTrucker}
                  className="gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add New
                </Button>
              </div>
              <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-2">
                {availableTruckers.map(trucker => {
                  const isSelected = (currentDailyEntry.trucks || []).some(t => t.trucker_id === trucker.id);
                  return (
                    <div key={trucker.id} className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded">
                      <Checkbox
                        id={`trucker-${trucker.id}`}
                        checked={isSelected}
                        onCheckedChange={() => toggleTruckerSelection(trucker.id)}
                      />
                      <label
                        htmlFor={`trucker-${trucker.id}`}
                        className="flex-1 text-sm cursor-pointer"
                      >
                        {trucker.name}
                        {trucker.truck_number && <span className="text-xs text-muted-foreground ml-1">({trucker.truck_number})</span>}
                        <span className="text-xs text-muted-foreground ml-1">- Rate: {trucker.rate}x</span>
                      </label>
                    </div>
                  );
                })}
                {availableTruckers.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-2">No truckers available</p>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <p className="font-medium">Selected:</p>
              <p className="text-muted-foreground">
                {(currentDailyEntry.workers || []).length} worker(s) •
                {(currentDailyEntry.grabman || []).length} grabman •
                {(currentDailyEntry.drivers || []).length} driver(s) •
                {(currentDailyEntry.trucks || []).length} truck(s)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeDailyEntryPopup}>
              Cancel
            </Button>
            <Button type="button" onClick={saveDailyEntry}>
              Save Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Worker Dialog */}
      <Dialog open={addWorkerOpen} onOpenChange={setAddWorkerOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add New {newWorkerType === 'cane_cutter' ? 'Worker' : newWorkerType === 'grabman' ? 'Grabman' : 'Driver'}
            </DialogTitle>
            <DialogDescription>
              Create a new {newWorkerType === 'cane_cutter' ? 'worker' : newWorkerType} to add to this daily entry
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new_worker_name">Name *</Label>
              <Input
                id="new_worker_name"
                value={newWorker.name}
                onChange={(e) => setNewWorker(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_worker_ssb">SSB Number</Label>
              <Input
                id="new_worker_ssb"
                value={newWorker.ssb_number}
                onChange={(e) => setNewWorker(prev => ({ ...prev, ssb_number: e.target.value }))}
                placeholder="Optional"
              />
            </div>
            {(newWorkerType === 'grabman' || newWorkerType === 'driver') && (
              <div className="space-y-2">
                <Label htmlFor="new_worker_rate">Rate *</Label>
                <Input
                  id="new_worker_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newWorker.rate}
                  onChange={(e) => setNewWorker(prev => ({ ...prev, rate: e.target.value }))}
                  placeholder="e.g. 1.5"
                />
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="new_worker_has_ssb"
                checked={newWorker.has_ssb}
                onCheckedChange={(checked) => setNewWorker(prev => ({ ...prev, has_ssb: checked }))}
              />
              <label htmlFor="new_worker_has_ssb" className="text-sm">
                Deduct SSB
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddWorkerOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNewWorker} disabled={savingInline}>
              {savingInline ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create & Select'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Trucker Dialog */}
      <Dialog open={addTruckerOpen} onOpenChange={setAddTruckerOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Trucker</DialogTitle>
            <DialogDescription>
              Create a new trucker to add to this daily entry
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new_trucker_name">Name *</Label>
              <Input
                id="new_trucker_name"
                value={newTrucker.name}
                onChange={(e) => setNewTrucker(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter trucker name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_trucker_truck">Truck Number</Label>
              <Input
                id="new_trucker_truck"
                value={newTrucker.truck_number}
                onChange={(e) => setNewTrucker(prev => ({ ...prev, truck_number: e.target.value }))}
                placeholder="e.g. T-001"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_trucker_rate">Rate</Label>
              <Input
                id="new_trucker_rate"
                type="number"
                step="0.01"
                min="0"
                value={newTrucker.rate}
                onChange={(e) => setNewTrucker(prev => ({ ...prev, rate: e.target.value }))}
                placeholder="1.0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddTruckerOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNewTrucker} disabled={savingInline}>
              {savingInline ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create & Select'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
